import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import type {
  FindingStatus,
  ReconciliationFinding,
  ReconciliationRun,
  ReconciliationSource,
  ReconciliationSummary,
  RunReconciliationResult,
} from '../types.ts'

export interface FindingsFilter {
  status?: FindingStatus
  source?: ReconciliationSource
  contractId?: string
  severity?: 'info' | 'warning' | 'critical'
}

export function useReconciliationSummary() {
  return useQuery<ReconciliationSummary>({
    queryKey: ['reconciliation', 'summary'],
    queryFn: () => apiClient.get('/reconciliation/summary').then((r) => r.data),
  })
}

export function useFindings(filter: FindingsFilter = {}) {
  return useQuery<ReconciliationFinding[]>({
    queryKey: ['reconciliation', 'findings', filter],
    queryFn: () =>
      apiClient
        .get('/reconciliation/findings', {
          params: {
            ...(filter.status ? { status: filter.status } : {}),
            ...(filter.source ? { source: filter.source } : {}),
            ...(filter.contractId ? { contractId: filter.contractId } : {}),
            ...(filter.severity ? { severity: filter.severity } : {}),
          },
        })
        .then((r) => r.data),
  })
}

export function useRuns(source?: ReconciliationSource) {
  return useQuery<ReconciliationRun[]>({
    queryKey: ['reconciliation', 'runs', source],
    queryFn: () =>
      apiClient
        .get('/reconciliation/runs', { params: source ? { source } : {} })
        .then((r) => r.data),
  })
}

export function useUpdateFinding() {
  const qc = useQueryClient()
  return useMutation<
    ReconciliationFinding,
    Error,
    { id: string; status?: FindingStatus; notes?: string }
  >({
    mutationFn: ({ id, ...payload }) =>
      apiClient.patch(`/reconciliation/findings/${id}`, payload).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reconciliation'] })
    },
  })
}

export function useTriggerReconciliation() {
  const qc = useQueryClient()
  return useMutation<RunReconciliationResult, Error, ReconciliationSource>({
    mutationFn: (source) =>
      apiClient.post('/reconciliation/run', { source }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reconciliation'] })
    },
  })
}
